/**
 * Orchestrates itinerary list search, offer fetch, and pricing reevaluation.
 *
 * @remarks Keeps AbortControllers and race guards in one façade so search / offer /
 * pricing cannot diverge. Waits for eager persistence before search/offer so pricing
 * prefs (e.g. half-fare) are not sent as schema defaults. Colocated with `itineraryList/`.
 */
import { useIonToast } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { getAccessTokenBirthdate, peekAuthState } from "../../../services/auth/auth-session.helpers";
import { beginNewPlannerItineraryFlow, getCorrelationFlowSnapshot } from "../../../services/http/correlationFlowStore";
import { ItineraryService, TransportMode } from "../../../services/ItineraryService";
import { rawGpsDataService } from "../../../services/navigation";
import { appSessionRestoreSlice, ephemeralStore, itineraryOfferRefreshSlice, itineraryPanelStateSlice, useEphemeralSelector, useEphemeralSliceSetter } from "../../../store/ephemeral";
import { bikePrefsSlice, carPrefsSlice, pricingPrefsSlice, rentalVehiclesPrefsSlice, transitPrefsSlice, usePersistedSlice, usePersistenceReady } from "../../../store/persistence";
import { useLayoutRef } from "../../../store/shared/hooks/useLayoutRef";
import { convertBikeSpeedKmhToBackend } from "../../../tools/BikeSpeedConversionTools";
import { toCarTransitAccessRequestFields } from "../../../tools/carPrefs.tools";
import { convertGeocodeItemToSearchResultItem } from "../../../tools/GeocodeTools";
import {
    captureCffPricingPrefsSnapshot,
    haveCffPricingPrefsChanged,
    reEvaluateItinerariesListPricing,
    type CffPricingPrefsSnapshot,
} from "../../../tools/itineraryListPricingReevaluation.tools";
import { buildOfferPricingPreferences } from "../../../tools/pricingPrefs.tools";
import { isRentalRoutingEnabled } from "../../../tools/rentalVehiclePrefs.tools";
import { resolveSearchDateForNewSearch } from "../../../tools/searchDate.tools";
import { toTransitModeFilters } from "../../../tools/transitPrefs.tools";
import { type ItinerarySearchRequestDTO } from "../../../types/dtos/api/itinerary/itinerary-search-request.dto";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type PricingEvaluationResponseDTO } from "../../../types/dtos/api/pricing/pricing-evaluation.dto";
import { GeocodeSource } from "../../../types/geocode-ui.types";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { buildItineraryCountryParams } from "../../../utils/countryUtils";
import { useItineraryDebugRelaunch } from "../../Debug/itineraryDebug";
import {
    buildSearchModes,
    getEarlierTransitPageArriveByMs,
    getTransitPageAnchorTimeMs,
    isAbortError,
    isCompleteMyCarItinerary,
    isValidBikeSpeed,
    mergeItineraryPatchById,
    mergeTransitPageItineraries,
    replaceItineraryInList,
    selectNewTransitPageItineraries,
    stripItineraryPricingAndOfferData,
    toItineraryRecordById,
    toSortedArray,
    TRANSIT_PAGE_EARLIER_ARRIVE_BY_OFFSET_MS,
    TRANSIT_PAGE_EARLIER_MAX_REQUESTS,
    TRANSIT_PAGE_EARLIER_TARGET_COUNT,
    type OfferFetchStatus,
    type TransitPageDirection,
    type TransitPageResult,
} from "./itineraryList.tools";

const itineraryService = new ItineraryService();

/**
 * Returns whether two pricing-preference snapshots disagree on Swiss reduction card.
 *
 * @param left Snapshot from the in-flight offer request.
 * @param right Current pricing preferences.
 * @returns `true` when `swissReductionCard` differs (including undefined asymmetry).
 */
const hasSwissReductionCardChanged = (
    left: ItineraryPricingPreferencesDTO["swissReductionCard"],
    right: ItineraryPricingPreferencesDTO["swissReductionCard"],
): boolean => {
    return left !== right;
};

/** Parameters for {@link useItineraryListDataFlow}. */
export interface UseItineraryListDataFlowParams {
    "selectedAddress":             ContainerProps["selectedAddress"];
    "date":                        ContainerProps["date"];
    "setDate":                     ContainerProps["setDate"];
    "setIsEditedDate":             ContainerProps["setIsEditedDate"];
    "departureSelectedAddress":    ContainerProps["departureSelectedAddress"];
    "selectedInputs":              ContainerProps["selectedInputs"];
    "selectedItinerary":           ContainerProps["selectedItinerary"];
    "setSelectedItinerary":        ContainerProps["setSelectedItinerary"];
    "itineraries":                 ContainerProps["itineraries"];
    "setItineraries":              ContainerProps["setItineraries"];
    "setITakeMyCar":               ContainerProps["setITakeMyCar"];
    "setITakeMyCarData":           ContainerProps["setITakeMyCarData"];
    "setShowArrowTop":             ContainerProps["setShowArrowTop"];
    "onResetPanelExpanded":        ContainerProps["onResetPanelExpanded"];
    "setVisible":                  ContainerProps["setVisible"];
    "itineraryListContentMode":    ContainerProps["itineraryListContentMode"];
    "setItineraryListContentMode": ContainerProps["setItineraryListContentMode"];
    "isPricingPrefsPanelOpen":     ContainerProps["isPricingPrefsPanelOpen"];
}

/** Result of {@link useItineraryListDataFlow}. */
export interface UseItineraryListDataFlowResult {
    "loading":                         boolean;
    "loadingItinerary":                boolean;
    "loadingPricing":                  boolean;
    "travelProgressRevealActive":      boolean;
    "skipListViewEnterAfterSearch":    boolean;
    "setSkipListViewEnterAfterSearch": Dispatch<SetStateAction<boolean>>;
    "offerStatusByItineraryId":        Record<string, OfferFetchStatus>;
    "setOfferStatusByItineraryId":     Dispatch<SetStateAction<Record<string, OfferFetchStatus>>>;
    "selectedItineraryOfferStatus":    OfferFetchStatus;
    "itineraryPricingFromPrefs":       ItineraryPricingPreferencesDTO;
    "wearCostPerKmChf":                number;
    "hasValidCoordinates":             boolean;
    "requestOfferForItinerary":        ( itinerary: ItineraryDTO ) => void;
    "itineraryDebugRelaunch":          ReturnType<typeof useItineraryDebugRelaunch>;
    "presentErrorToast":               ( message: string ) => void;
    "presentSuccessToast":             ( message: string ) => void;
    "loadTransitPage":                 ( direction: TransitPageDirection ) => void;
    "transitPageLoading":              TransitPageDirection | null;
    "transitPageExhaustedEarlier":     boolean;
    "transitPageExhaustedLater":       boolean;
    "transitPagePricingPendingIds":    ReadonlySet<string>;
    "transitPageNewBatch":             {

        /** Itinerary ids of the last merged page, sorted by `startTime` (display order). */
        "orderedIds": readonly string[];

        /** Direction the page was loaded in — drives the slide-in orientation. */
        "direction": TransitPageDirection;
    } | null;
}

/**
 * Coordinates OD search, completion, pricing evaluation, offer fetch, and CFF reevaluation.
 *
 * @param params Planner inputs and Home setters required by the search pipeline.
 * @returns Loading flags, offer status, pricing prefs DTO, and offer/debug helpers.
 */
export const useItineraryListDataFlow = ( params: UseItineraryListDataFlowParams ): UseItineraryListDataFlowResult => {
    const {
        selectedAddress,
        date,
        setDate,
        setIsEditedDate,
        departureSelectedAddress,
        selectedInputs,
        selectedItinerary,
        setSelectedItinerary,
        itineraries,
        setItineraries,
        setITakeMyCar,
        setITakeMyCarData,
        setShowArrowTop,
        onResetPanelExpanded,
        setVisible,
        itineraryListContentMode,
        setItineraryListContentMode,
        isPricingPrefsPanelOpen,
    } = params;

    const isSavedListMode = itineraryListContentMode === "saved";

    const [ loading, setLoading ] = useState( false );
    const requestId = useRef( 0 );
    const activeRequestAbortControllerRef = useRef<AbortController | null>( null );

    /**
     * Incremental transit paging state ("load earlier/later" pages).
     * `transitPageLoadingRef` mirrors the state for synchronous in-flight guards.
     */
    const [ transitPageLoading, setTransitPageLoading ] = useState<TransitPageDirection | null>( null );
    const [ transitPageExhausted, setTransitPageExhausted ] = useState<{
        "earlier": boolean;
        "later":   boolean;
    }>( {
        "earlier": false,
        "later":   false,
    } );
    const transitPageLoadingRef = useRef<TransitPageDirection | null>( null );
    const transitPageAbortControllerRef = useRef<AbortController | null>( null );

    /**
     * Deepest arrive-by bound already scanned by the "earlier" chain (epoch ms).
     * Persisted across clicks so a click whose pages were fully filtered out keeps digging
     * deeper instead of restarting from the first displayed transit arrival.
     */
    const earlierArriveByBoundRef = useRef<number | null>( null );

    /** Ids of paged transit items whose pricing evaluation is still in flight. */
    const [ transitPagePricingPendingIds, setTransitPagePricingPendingIds ] = useState<ReadonlySet<string>>( new Set() );

    /** Ids (display order) + direction of the last merged transit page; drives row mount animation. */
    const [ transitPageNewBatch, setTransitPageNewBatch ] = useState<{
        "orderedIds": readonly string[];
        "direction":  TransitPageDirection;
    } | null>( null );

    /** Last OD search request; reused with shifted time/modes for transit paging. */
    const lastSearchRequestRef = useRef<ItinerarySearchRequestDTO | null>( null );

    const [ travelProgressRevealActive, setTravelProgressRevealActive ] = useState( false );
    const [ skipListViewEnterAfterSearch, setSkipListViewEnterAfterSearch ] = useState( false );

    const [ offerStatusByItineraryId, setOfferStatusByItineraryId ] = useState<Record<string, OfferFetchStatus>>( {} );
    const inFlightOfferRequestByItineraryIdRef = useRef<Record<string, boolean>>( {} );
    const offerRequestAbortControllerRef = useRef<AbortController | null>( null );

    /**
     * Swiss reduction card sent with the currently in-flight offer request.
     * Used to detect a prefs change that must abort and replace the request.
     */
    const inFlightOfferSwissReductionCardRef = useRef<ItineraryPricingPreferencesDTO["swissReductionCard"]>( undefined );

    /** Itinerary id of the active offer request (for safe inFlight cleanup after abort). */
    const activeOfferItineraryIdRef = useRef<string | null>( null );

    /**
     * Offer requested before eager persistence finished; flushed once `persistenceReady` is true.
     */
    const pendingOfferItineraryRef = useRef<ItineraryDTO | null>( null );

    /**
     * Panel reset is a side effect of a new search, not a search input.
     * A ref keeps layout-driven callback identity changes (resize, zoom) from retriggering `loadData`.
     */
    const onResetPanelExpandedRef = useLayoutRef( onResetPanelExpanded );

    const persistenceReady = usePersistenceReady();
    const persistenceReadyRef = useRef( persistenceReady );

    const [ bikePrefs ] = usePersistedSlice( bikePrefsSlice );
    const [ carPrefs ] = usePersistedSlice( carPrefsSlice );
    const [ pricingPrefs ] = usePersistedSlice( pricingPrefsSlice );
    const [ rentalVehiclesPrefs ] = usePersistedSlice( rentalVehiclesPrefsSlice );
    const isMyCarSearchEnabled = carPrefs.carUsageMode !== "no_car";
    const [ transitPrefs ] = usePersistedSlice( transitPrefsSlice );
    const { wearCostPerKmChf, swissReductionCard, displayCurrency } = pricingPrefs;
    const setItineraryPanel = useEphemeralSliceSetter( itineraryPanelStateSlice );

    /**
     * Skips the first OD search effect run when ItineraryList mounts already in saved mode
     * (opening the saved panel remounts the list while leftover OD coords are still valid).
     * Consumed on the first post-date-normalization pass so a later OD/pref change while
     * saved is open may still run search and switch the panel to search results.
     */
    const skipOdSearchOnceOnSavedMountRef = useRef( isSavedListMode );

    const loadingItinerary = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.loadingItinerary ) === true;
    const loadingPricing = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.loadingPricing ) === true;
    const setLoadingItinerary = useCallback( ( loading: boolean ): void => {
        setItineraryPanel( ( previous ) => ( {
            ...previous,
            "loadingItinerary": loading,
        } ) );
    }, [ setItineraryPanel ] );
    const setLoadingPricing = useCallback( ( loading: boolean ): void => {
        setItineraryPanel( ( previous ) => ( {
            ...previous,
            "loadingPricing": loading,
        } ) );
    }, [ setItineraryPanel ] );

    /**
     * Ends the search loader and triggers TravelProgress segment reveal.
     *
     * @param itineraryCount Number of itineraries returned by the search.
     */
    const completeItinerarySearchLoading = useCallback( ( itineraryCount: number ): void => {
        setLoading( false );
        setSkipListViewEnterAfterSearch( true );

        if ( itineraryCount <= 0 ) {
            setTravelProgressRevealActive( false );

            return;
        }

        setTravelProgressRevealActive( false );
        queueMicrotask( () => {
            setTravelProgressRevealActive( true );
        } );
    }, [] );

    useEffect( () => {
        if ( !travelProgressRevealActive ) {
            return;
        }

        const resetRevealTimer = window.setTimeout( () => {
            setTravelProgressRevealActive( false );
        }, 900 );

        return () => {
            window.clearTimeout( resetRevealTimer );
        };
    }, [ travelProgressRevealActive ] );

    /**
     * The new-batch descriptor only needs to outlive the last staggered row entrance;
     * clearing it afterwards keeps remounted rows from replaying the animation.
     */
    useEffect( () => {
        if ( transitPageNewBatch === null ) {
            return;
        }

        const entranceWindowMs = transitPageNewBatch.orderedIds.length * UI_MOTION.itineraryPanelStaggerItemMs +
          UI_MOTION.duration.fastMs +
          400;

        const clearBatchTimer = window.setTimeout( () => {
            setTransitPageNewBatch( null );
        }, entranceWindowMs );

        return () => {
            window.clearTimeout( clearBatchTimer );
        };
    }, [ transitPageNewBatch ] );

    const hasPersonalBike = bikePrefs.isPersonalBike === true;
    const bikeSpeedKmh = hasPersonalBike && isValidBikeSpeed( bikePrefs.bikeSpeedKmh )
        ? bikePrefs.bikeSpeedKmh
        : 25;
    const enabledRentalVehicleTypes = rentalVehiclesPrefs.enabledTypes;
    const hasRentalTypes = isRentalRoutingEnabled( enabledRentalVehicleTypes );
    const selectedSwissReductionCard: ItineraryPricingPreferencesDTO["swissReductionCard"] = swissReductionCard === "half-fare"
        ? "half-fare"
        : swissReductionCard === "ga"
            ? "ga"
            : "none";
    const transportPersonalBike = bikePrefs.transportPersonalBike;
    const itineraryPricingFromPrefs: ItineraryPricingPreferencesDTO = useMemo( () => buildOfferPricingPreferences( pricingPrefs, {
        "isPersonalBike": hasPersonalBike,
        transportPersonalBike,
    }, getAccessTokenBirthdate( peekAuthState().accessToken ?? "" ) ), [ hasPersonalBike, pricingPrefs, transportPersonalBike ] );
    const pricingPreferencesRef = useRef<ItineraryPricingPreferencesDTO>( itineraryPricingFromPrefs );
    const pricingPrefsRef = useRef( pricingPrefs );
    const selectedItineraryRef = useRef( selectedItinerary );
    const selectedSwissReductionCardRef = useRef( selectedSwissReductionCard );
    const previousSwissReductionCardRef = useRef<ItineraryPricingPreferencesDTO["swissReductionCard"]>( selectedSwissReductionCard );
    const itinerariesRef = useRef( itineraries );
    const pricingReevaluationAbortControllerRef = useRef<AbortController | null>( null );
    const cffPricingPrefsSnapshotRef = useRef<CffPricingPrefsSnapshot | null>( null );

    /**
     * When the user switches to GA, stores the prior non-`ga` reduction so a return to that exact
     * reduction can skip an offer refetch for the current selected itinerary.
     */
    const swissReductionSnapshotBeforeGaRef = useRef<ItineraryPricingPreferencesDTO["swissReductionCard"] | null>( null );
    const hasValidCoordinates =
        selectedInputs.departure.lat !== 0 &&
        selectedInputs.departure.lon !== 0 &&
        selectedInputs.arrival.lat !== 0 &&
        selectedInputs.arrival.lon !== 0;

    // Pour les messages toastCorrespondence
    const [ present ] = useIonToast();
    const presentRef = useRef( present );

    useEffect( () => {
        presentRef.current = present;
    }, [ present ] );

    useEffect( () => {
        persistenceReadyRef.current = persistenceReady;
    }, [ persistenceReady ] );

    useEffect( () => {
        pricingPreferencesRef.current = itineraryPricingFromPrefs;
    }, [ itineraryPricingFromPrefs ] );

    useEffect( () => {
        pricingPrefsRef.current = pricingPrefs;
    }, [ pricingPrefs ] );

    useEffect( () => {
        selectedItineraryRef.current = selectedItinerary;
    }, [ selectedItinerary ] );

    useEffect( () => {
        selectedSwissReductionCardRef.current = selectedSwissReductionCard;
    }, [ selectedSwissReductionCard ] );

    useEffect( () => {
        itinerariesRef.current = itineraries;
    }, [ itineraries ] );

    /**
     * Replaces an itinerary (matched by `id`) in the list and in the selection with its enriched version.
     *
     * @param enrichedItinerary - Itinerary returned by `POST /api/sales/offer`.
     */
    const applyEnrichedItinerary = useCallback( ( enrichedItinerary: ItineraryDTO ): void => {
        setItineraries( ( previousItineraries ) => {
            return previousItineraries.map( ( previousItinerary ) => {
                return previousItinerary.id === enrichedItinerary.id ? enrichedItinerary : previousItinerary;
            } );
        } );
        setSelectedItinerary( ( previousSelectedItinerary ) => {
            if ( previousSelectedItinerary === null || previousSelectedItinerary.id !== enrichedItinerary.id ) {
                return previousSelectedItinerary;
            }

            return enrichedItinerary;
        } );
    }, [ setItineraries, setSelectedItinerary ] );

    const externalOfferRefresh = useEphemeralSelector( itineraryOfferRefreshSlice, ( state ) => state );

    /** Last merged refresh revision; starts at the mount value so an older refresh is never re-applied. */
    const appliedOfferRefreshRevisionRef = useRef( externalOfferRefresh.revision );

    /**
     * Merges itineraries whose offer was refreshed outside the planner (ticket purchase wizard).
     */
    useEffect( () => {
        if ( externalOfferRefresh.itinerary === null || externalOfferRefresh.revision === appliedOfferRefreshRevisionRef.current ) {
            return;
        }

        appliedOfferRefreshRevisionRef.current = externalOfferRefresh.revision;
        applyEnrichedItinerary( externalOfferRefresh.itinerary );
    }, [ applyEnrichedItinerary, externalOfferRefresh ] );

    const requestOfferForItinerary = useCallback( ( itinerary: ItineraryDTO ): void => {
        if ( persistenceReadyRef.current !== true ) {
            pendingOfferItineraryRef.current = itinerary;

            return;
        }

        pendingOfferItineraryRef.current = null;

        const requestedSwissReductionCard = pricingPreferencesRef.current.swissReductionCard;

        if ( inFlightOfferRequestByItineraryIdRef.current[itinerary.id] === true ) {
            if ( hasSwissReductionCardChanged(
                inFlightOfferSwissReductionCardRef.current,
                requestedSwissReductionCard,
            ) === false ) {
                setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                    ...previousStatusById,
                    [itinerary.id]: "loading",
                } ) );

                return;
            }

            // Prefs changed while this itinerary's offer is in flight: abort and replace below.
            if ( offerRequestAbortControllerRef.current !== null ) {
                offerRequestAbortControllerRef.current.abort();
            }
        } else if ( offerRequestAbortControllerRef.current !== null ) {
            offerRequestAbortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        offerRequestAbortControllerRef.current = abortController;
        activeOfferItineraryIdRef.current = itinerary.id;
        inFlightOfferRequestByItineraryIdRef.current[itinerary.id] = true;
        inFlightOfferSwissReductionCardRef.current = requestedSwissReductionCard;
        setOfferStatusByItineraryId( ( previousStatusById ) => ( {
            ...previousStatusById,
            [itinerary.id]: "loading",
        } ) );

        void itineraryService.evaluateItineraryOffer( itinerary, pricingPreferencesRef.current, abortController.signal ).
            then( ( response ) => {
                applyEnrichedItinerary( response.itinerary );
                setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                    ...previousStatusById,
                    [itinerary.id]: "success",
                } ) );
            } ).
            catch( ( error: unknown ) => {
                if ( isAbortError( error ) ) {
                    return;
                }

                console.error( "[ItineraryList] Offer evaluation failed", {
                    ...getCorrelationFlowSnapshot(),
                    "reason": error,
                } );
                setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                    ...previousStatusById,
                    [itinerary.id]: "error",
                } ) );
            } ).
            finally( () => {
                if ( offerRequestAbortControllerRef.current === abortController ) {
                    offerRequestAbortControllerRef.current = null;
                    inFlightOfferRequestByItineraryIdRef.current[itinerary.id] = false;
                    inFlightOfferSwissReductionCardRef.current = undefined;
                    activeOfferItineraryIdRef.current = null;

                    return;
                }

                /*
                 * Superseded by another itinerary's offer: clear this id's inFlight flag.
                 * Same-itinerary prefs retry leaves inFlight true for the replacement request.
                 */
                if ( activeOfferItineraryIdRef.current !== itinerary.id ) {
                    inFlightOfferRequestByItineraryIdRef.current[itinerary.id] = false;
                }
            } );
    }, [ applyEnrichedItinerary ] );

    /**
     * Flushes an offer click that happened before eager persistence finished hydrating.
     */
    useEffect( () => {
        if ( persistenceReady !== true ) {
            return;
        }

        const pendingItinerary = pendingOfferItineraryRef.current;
        if ( pendingItinerary === null || isSavedListMode ) {
            return;
        }

        pendingOfferItineraryRef.current = null;
        requestOfferForItinerary( pendingItinerary );
    }, [ isSavedListMode, persistenceReady, requestOfferForItinerary ] );

    const abortInFlightPricing = useCallback( (): void => {
        if ( pricingReevaluationAbortControllerRef.current !== null ) {
            pricingReevaluationAbortControllerRef.current.abort();
            pricingReevaluationAbortControllerRef.current = null;
        }
    }, [] );

    const abortInFlightOffers = useCallback( ( itineraryIds: readonly string[] ): void => {
        if ( offerRequestAbortControllerRef.current !== null ) {
            offerRequestAbortControllerRef.current.abort();
            offerRequestAbortControllerRef.current = null;
        }

        inFlightOfferSwissReductionCardRef.current = undefined;
        activeOfferItineraryIdRef.current = null;

        for ( const itineraryId of itineraryIds ) {
            inFlightOfferRequestByItineraryIdRef.current[itineraryId] = false;
        }
    }, [] );

    const presentErrorToast = useCallback( ( message: string ): void => {
        void presentRef.current( {
            message,
            "duration": 2500,
            "color":    "danger",
        } );
    }, [] );

    const presentSuccessToast = useCallback( ( message: string ): void => {
        void presentRef.current( {
            message,
            "duration": 2000,
            "color":    "success",
        } );
    }, [] );

    /**
     * Loads one page of transit itineraries before/after the displayed results.
     *
     * @remarks Re-runs `/api/itinerary/search` with `modes:[TRANSIT]`. `later` searches
     * departures from the last displayed transit departure; `earlier` chains arrive-by
     * pages bounded at `firstTransit.endTime - 1min` (then the earliest returned arrival
     * minus 1 minute) and keeps journeys departing before the first displayed transit,
     * whatever their arrival time. The deepest scanned bound is persisted in
     * `earlierArriveByBoundRef` so consecutive clicks keep digging instead of restarting.
     * Non-transit items never contribute the anchor and are never re-fetched. New items
     * get a silent pricing evaluation so price badges stay consistent with the first page.
     *
     * @param direction Page direction relative to the displayed list.
     */
    const loadTransitPage = useCallback( ( direction: TransitPageDirection ): void => {
        const baseRequest = lastSearchRequestRef.current;
        if ( baseRequest === null || transitPageLoadingRef.current !== null ) {
            return;
        }

        const anchorMs = getTransitPageAnchorTimeMs( itinerariesRef.current, direction );
        if ( anchorMs === null ) {
            return;
        }

        /*
         * "earlier" resumes from the deepest scanned arrive-by bound when set, otherwise
         * starts just before the first displayed transit arrival.
         */
        const firstBoundMs = direction === "earlier"
            ? earlierArriveByBoundRef.current ?? getEarlierTransitPageArriveByMs( itinerariesRef.current )
            : null;
        if ( direction === "earlier" && firstBoundMs === null ) {
            return;
        }

        const buildPageRequest = ( timestampMs: number ): ItinerarySearchRequestDTO => ( {
            ...baseRequest,
            "modes": [ TransportMode.TRANSIT ],
            "time":  {
                "timestamp":            timestampMs,
                "isDepartureTimestamp": direction === "later",
            },
        } );

        if ( transitPageAbortControllerRef.current !== null ) {
            transitPageAbortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        transitPageAbortControllerRef.current = abortController;
        transitPageLoadingRef.current = direction;
        setTransitPageLoading( direction );

        /*
         * One arrive-by page can be dominated by departures at/after the anchor arriving
         * before the bound — they are all filtered out client-side. Chain deeper pages
         * (bound = earliest returned arrival - 1min) until enough earlier departures are
         * collected, the schedule is exhausted, or the per-click cap is reached.
         */
        const fetchEarlierTransitPage = async(): Promise<TransitPageResult> => {
            const collected: ItineraryDTO[] = [];
            let boundMs = firstBoundMs as number;
            let pageExhausted = false;

            for (
                let round = 0;
                round < TRANSIT_PAGE_EARLIER_MAX_REQUESTS && collected.length < TRANSIT_PAGE_EARLIER_TARGET_COUNT;
                round += 1
            ) {
                if ( abortController.signal.aborted ) {
                    break;
                }

                const response = await itineraryService.searchItinerary(
                    buildPageRequest( boundMs ),
                    { "signal": abortController.signal },
                );
                if ( abortController.signal.aborted ) {
                    break;
                }

                const pageItineraries = toSortedArray( response.itineraries );
                if ( pageItineraries.length === 0 ) {
                    pageExhausted = true;
                    break;
                }

                collected.push( ...selectNewTransitPageItineraries(
                    [ ...itinerariesRef.current, ...collected ],
                    pageItineraries,
                    "earlier",
                    anchorMs,
                ) );

                const minEndMs = Math.min( ...pageItineraries.map( ( itinerary ) => Date.parse( itinerary.endTime ) ).filter( Number.isFinite ) );
                if ( !Number.isFinite( minEndMs ) ) {
                    break;
                }
                boundMs = minEndMs - TRANSIT_PAGE_EARLIER_ARRIVE_BY_OFFSET_MS;
                earlierArriveByBoundRef.current = boundMs;
            }

            return {
                "items":     collected,
                "exhausted": pageExhausted,
            };
        };

        const pagePromise: Promise<TransitPageResult> = direction === "earlier"
            ? fetchEarlierTransitPage()
            : itineraryService.searchItinerary( buildPageRequest( anchorMs ), { "signal": abortController.signal } ).
                then( ( response ): TransitPageResult => {
                    if ( abortController.signal.aborted ) {
                        return {
                            "items":     [],
                            "exhausted": false,
                        };
                    }
                    const items = selectNewTransitPageItineraries(
                        itinerariesRef.current,
                        toSortedArray( response.itineraries ),
                        "later",
                    );

                    return {
                        items,
                        "exhausted": items.length === 0,
                    };
                } );

        void pagePromise.
            then( ( pageResult ) => {
                if ( abortController.signal.aborted ) {
                    return;
                }

                if ( pageResult.exhausted ) {
                    // Empty window: stop asking for more pages in this direction.
                    setTransitPageExhausted( ( previousExhausted ) => ( {
                        ...previousExhausted,
                        [direction]: true,
                    } ) );
                }

                const newTransitItineraries = pageResult.items;
                if ( newTransitItineraries.length === 0 ) {
                    return;
                }

                setItineraries( ( previousItineraries ) => {
                    return mergeTransitPageItineraries( previousItineraries, newTransitItineraries, direction );
                } );

                // Record the batch in display order so the newly mounted rows can stagger in.
                setTransitPageNewBatch( {
                    "orderedIds": [ ...newTransitItineraries ].
                        sort( ( a, b ) => Date.parse( a.startTime ) - Date.parse( b.startTime ) ).
                        map( ( itinerary ) => itinerary.id ),
                    direction,
                } );

                // Mark the new rows so their price pill shows the loading animation while evaluation runs.
                const newItineraryIds = newTransitItineraries.map( ( itinerary ) => itinerary.id );
                setTransitPagePricingPendingIds( ( previousPendingIds ) => {
                    return new Set( [ ...previousPendingIds, ...newItineraryIds ] );
                } );

                void itineraryService.evaluateItinerariesPricing(
                    toItineraryRecordById( newTransitItineraries ),
                    pricingPreferencesRef.current,
                    abortController.signal,
                ).then( ( pricingResult: PricingEvaluationResponseDTO ) => {
                    if ( abortController.signal.aborted ) {
                        return;
                    }

                    setItineraries( ( previousItineraries ) => {
                        return mergeItineraryPatchById( previousItineraries, pricingResult.itineraries );
                    } );
                } ).
                    catch( ( error: unknown ) => {
                        if ( !isAbortError( error ) ) {
                            console.error( "[ItineraryList] Transit page pricing failed", {
                                ...getCorrelationFlowSnapshot(),
                                "reason": error,
                            } );
                        }
                    } ).
                    finally( () => {
                        setTransitPagePricingPendingIds( ( previousPendingIds ) => {
                            const nextPendingIds = new Set( previousPendingIds );
                            for ( const itineraryId of newItineraryIds ) {
                                nextPendingIds.delete( itineraryId );
                            }

                            return nextPendingIds;
                        } );
                    } );
            } ).
            catch( ( error: unknown ) => {
                if ( isAbortError( error ) ) {
                    return;
                }

                console.error( "[ItineraryList] Transit page search failed", {
                    ...getCorrelationFlowSnapshot(),
                    "reason": error,
                } );
                presentErrorToast( "Impossible de charger plus d'itinéraires" );
            } ).
            finally( () => {
                if ( transitPageAbortControllerRef.current === abortController ) {
                    transitPageAbortControllerRef.current = null;
                    transitPageLoadingRef.current = null;
                    setTransitPageLoading( null );
                }
            } );
    }, [ presentErrorToast, setItineraries ] );

    const itineraryDebugRelaunch = useItineraryDebugRelaunch( {
        itineraryService,
        "pricingPreferences": itineraryPricingFromPrefs,
        requestOfferForItinerary,
        itineraries,
        selectedItinerary,
        setItineraries,
        setSelectedItinerary,
        setOfferStatusByItineraryId,
        setLoadingPricing,
        loadingPricing,
        offerStatusByItineraryId,
        abortInFlightPricing,
        abortInFlightOffers,
        presentErrorToast,
    } );

    /**
     * Re-evaluates list pricing when CFF prefs change after the pricing modal closes.
     * Changes made while the modal is open are batched until dismiss.
     *
     * @remarks When details are open and an offer will be refetched, the open itinerary is
     * omitted from `/api/pricing/evaluation` so it does not race with `/api/sales/offer`.
     */
    useEffect( () => {
        if ( isPricingPrefsPanelOpen ) {
            cffPricingPrefsSnapshotRef.current = captureCffPricingPrefsSnapshot( pricingPrefsRef.current );

            return;
        }

        const snapshot = cffPricingPrefsSnapshotRef.current;
        cffPricingPrefsSnapshotRef.current = null;
        if ( snapshot === null ) {
            return;
        }

        if ( !haveCffPricingPrefsChanged( snapshot, captureCffPricingPrefsSnapshot( pricingPrefsRef.current ) ) ) {
            return;
        }

        const currentItineraries = itinerariesRef.current;
        if ( currentItineraries.length === 0 ) {
            return;
        }

        const itineraryIds = currentItineraries.map( ( itinerary ) => itinerary.id );
        abortInFlightPricing();
        abortInFlightOffers( itineraryIds );

        const strippedItineraries = currentItineraries.map( stripItineraryPricingAndOfferData );
        setItineraries( strippedItineraries );
        itinerariesRef.current = strippedItineraries;

        const selectedItinerarySnapshot = selectedItineraryRef.current;
        let strippedSelectedForOffer: ItineraryDTO | null = null;
        if ( selectedItinerarySnapshot !== null ) {
            const strippedSelected = strippedItineraries.find( ( itinerary ) => {
                return itinerary.id === selectedItinerarySnapshot.id;
            } ) ?? stripItineraryPricingAndOfferData( selectedItinerarySnapshot );
            setSelectedItinerary( strippedSelected );
            selectedItineraryRef.current = strippedSelected;
            strippedSelectedForOffer = strippedSelected;
        }

        /*
         * Details + offer refetch: evaluate every other itinerary only. Including the open
         * itinerary races with `/sales/offer`, which already refreshes its pricing fields.
         * List mode, GA, and saved mode keep the selected row in the evaluation batch.
         */
        const shouldRefetchOffer = strippedSelectedForOffer !== null &&
          selectedSwissReductionCardRef.current !== "ga" &&
          !isSavedListMode;
        const itinerariesForEvaluation = shouldRefetchOffer && strippedSelectedForOffer !== null
            ? strippedItineraries.filter( ( itinerary ) => itinerary.id !== strippedSelectedForOffer.id )
            : strippedItineraries;

        const abortController = new AbortController();
        pricingReevaluationAbortControllerRef.current = abortController;

        /*
         * Local offer-status state must not be written synchronously in the effect body
         * (`react-hooks/set-state-in-effect`). Clear stale success flags before paint.
         */
        queueMicrotask( () => {
            if ( abortController.signal.aborted ) {
                return;
            }

            setOfferStatusByItineraryId( {} );

            if ( shouldRefetchOffer && strippedSelectedForOffer !== null ) {
                requestOfferForItinerary( strippedSelectedForOffer );
            }
        } );

        if ( itinerariesForEvaluation.length === 0 ) {
            return () => {
                abortController.abort();
            };
        }

        setLoadingPricing( true );

        void reEvaluateItinerariesListPricing( {
            itineraryService,
            "itineraries":        itinerariesForEvaluation,
            "pricingPreferences": pricingPreferencesRef.current,
            "signal":             abortController.signal,
        } ).then( ( pricingResult: PricingEvaluationResponseDTO ) => {
            if ( abortController.signal.aborted ) {
                return;
            }

            setItineraries( ( previousItineraries ) => {
                return mergeItineraryPatchById( previousItineraries, pricingResult.itineraries );
            } );

            /*
             * Only sync selection from evaluation when the open itinerary was part of the
             * batch (no concurrent offer). Offer responses already replace selectedItinerary.
             */
            if ( !shouldRefetchOffer ) {
                setSelectedItinerary( ( previousSelectedItinerary ) => {
                    if ( previousSelectedItinerary === null ) {
                        return previousSelectedItinerary;
                    }

                    const patchedSelected = pricingResult.itineraries[previousSelectedItinerary.id];
                    if ( patchedSelected === undefined ) {
                        return previousSelectedItinerary;
                    }

                    return patchedSelected;
                } );
            }
        } ).
            catch( ( error: unknown ) => {
                if ( isAbortError( error ) ) {
                    return;
                }

                console.error( "[ItineraryList] Pricing re-evaluation failed", {
                    ...getCorrelationFlowSnapshot(),
                    "reason": error,
                } );
            } ).
            finally( () => {
                if ( abortController.signal.aborted === false ) {
                    setLoadingPricing( false );
                }
            } );

        return () => {
            abortController.abort();
        };
    }, [
        abortInFlightOffers,
        abortInFlightPricing,
        isSavedListMode,
        isPricingPrefsPanelOpen,
        requestOfferForItinerary,
        setItineraries,
        setLoadingPricing,
        setSelectedItinerary,
    ] );

    // Load itinerary data when inputs change
    useEffect( () => {
        // Wait for eager slices (including pricingPrefs) so search/pricing use persisted half-fare.
        if ( persistenceReady !== true ) {
            return;
        }

        /*
         * Session restore is still busy applying its snapshot. Let it finish before
         * deciding whether to run a new search, otherwise the restored list/selection
         * would be overwritten while contributors are still writing state.
         */
        const appSessionRestore = ephemeralStore.peek( appSessionRestoreSlice );
        if ( appSessionRestore.isRestoring ) {
            return;
        }

        // Skip the first search after a session restore so the restored itinerary list is not overwritten.
        if ( appSessionRestore.skipNextItinerarySearch ) {
            ephemeralStore.setValue( appSessionRestoreSlice, ( previous ) => ( {
                ...previous,
                "skipNextItinerarySearch": false,
            } ) );

            return;
        }

        const searchDate = resolveSearchDateForNewSearch( date );
        if ( searchDate !== date ) {
            setDate( searchDate );
            setIsEditedDate( false );

            return;
        }

        // Opening saved remounts ItineraryList with leftover OD coords — skip that one pass only.
        if ( skipOdSearchOnceOnSavedMountRef.current ) {
            skipOdSearchOnceOnSavedMountRef.current = false;

            return;
        }

        let effectAbortController: AbortController | null = null;

        const loadData = async() => {
            if ( activeRequestAbortControllerRef.current !== null ) {
                activeRequestAbortControllerRef.current.abort();
            }
            const abortController = new AbortController();
            effectAbortController = abortController;
            activeRequestAbortControllerRef.current = abortController;

            // A new OD search invalidates transit paging: abort any page request and reset its state.
            if ( transitPageAbortControllerRef.current !== null ) {
                transitPageAbortControllerRef.current.abort();
                transitPageAbortControllerRef.current = null;
            }
            transitPageLoadingRef.current = null;
            lastSearchRequestRef.current = null;
            earlierArriveByBoundRef.current = null;
            setTransitPageLoading( null );
            setTransitPagePricingPendingIds( new Set() );
            setTransitPageNewBatch( null );
            setTransitPageExhausted( {
                "earlier": false,
                "later":   false,
            } );

            // Reset itinerary selection so the user sees the results list
            setItineraryListContentMode( "search" );
            setSelectedItinerary( null );
            setShowArrowTop( false );
            onResetPanelExpandedRef.current();
            setVisible( true );
            setLoading( true );
            setTravelProgressRevealActive( false );
            setSkipListViewEnterAfterSearch( false );
            setLoadingItinerary( true );
            setLoadingPricing( false );
            setItineraries( [] );
            setOfferStatusByItineraryId( {} );
            pendingOfferItineraryRef.current = null;

            const id = ++requestId.current;
            const isCurrentRequest = (): boolean => {
                return id === requestId.current && abortController.signal.aborted === false;
            };
            try {
                beginNewPlannerItineraryFlow();

                // Extract country and currency parameters from autocomplete data
                const departureSearchResult = selectedInputs.departureAddress
                    ? convertGeocodeItemToSearchResultItem( selectedInputs.departureAddress )
                    : null;
                const arrivalSearchResult = selectedInputs.arrivalAddress
                    ? convertGeocodeItemToSearchResultItem( selectedInputs.arrivalAddress )
                    : null;
                const { fromCountry, toCountry } = buildItineraryCountryParams(
                    departureSearchResult,
                    arrivalSearchResult,
                );

                // Always resolve "Ma position" from the latest raw GPS sample.
                const latestGps = rawGpsDataService.getLastValue();
                const isDepartureMyLocation =
                    departureSelectedAddress?.source === GeocodeSource.MYLOCATION ||
                    selectedInputs.departureAddress?.source === GeocodeSource.MYLOCATION;
                const isArrivalMyLocation =
                    selectedAddress?.source === GeocodeSource.MYLOCATION ||
                    selectedInputs.arrivalAddress?.source === GeocodeSource.MYLOCATION;

                // Keep previous sentinel fallback (-1) for backward compatibility.
                const fromLat = Number( isDepartureMyLocation || selectedInputs.departure.lat === -1
                    ? latestGps.latitude
                    : selectedInputs.departure.lat );
                const fromLon = Number( isDepartureMyLocation || selectedInputs.departure.lon === -1
                    ? latestGps.longitude
                    : selectedInputs.departure.lon );
                const toLat = Number( isArrivalMyLocation || selectedInputs.arrival.lat === -1
                    ? latestGps.latitude
                    : selectedInputs.arrival.lat );
                const toLon = Number( isArrivalMyLocation || selectedInputs.arrival.lon === -1
                    ? latestGps.longitude
                    : selectedInputs.arrival.lon );

                const searchModes = buildSearchModes( hasPersonalBike, hasRentalTypes, carPrefs.carpoolingEnabled );
                const searchRequest: ItinerarySearchRequestDTO = {
                    "from": {
                        "lat":     fromLat,
                        "lon":     fromLon,
                        "country": fromCountry ?? "CH",
                    },
                    "to": {
                        "lat":     toLat,
                        "lon":     toLon,
                        "country": toCountry ?? "CH",
                    },
                    "time": {
                        "timestamp":            searchDate.timestamp,
                        "isDepartureTimestamp": searchDate.isDepartureTimestamp,
                    },
                    "modes":                 searchModes,
                    "currency":              displayCurrency,
                    "havePersonalBike":      hasPersonalBike,
                    ...hasRentalTypes ? { "rentalVehicleTypes": [ ...enabledRentalVehicleTypes ] } : { "rentalVehicleTypes": [] },
                    ...hasPersonalBike ? { "bikeSpeedKmh": convertBikeSpeedKmhToBackend( bikeSpeedKmh ) } : {},
                    "elevationCosts":        bikePrefs.elevationCosts,
                    "transportPersonalBike": hasPersonalBike && bikePrefs.transportPersonalBike === true,
                    "tripEndByBike":         hasPersonalBike && bikePrefs.endItineraryByBike === true,
                    ...toCarTransitAccessRequestFields( carPrefs ),
                    "transitModeFilters":    toTransitModeFilters( transitPrefs.enabledModes ),
                    "maxTransfers":          transitPrefs.maxTransfers,
                };
                lastSearchRequestRef.current = searchRequest;

                const searchResponse = await itineraryService.searchItinerary( searchRequest, { "signal": abortController.signal } );

                /** Annule le traitement si on a lancé une autre requête entre temps */
                if ( !isCurrentRequest() ) {
                    return;
                }

                const sortedItineraries = toSortedArray( searchResponse.itineraries );
                setItineraries( sortedItineraries );
                completeItinerarySearchLoading( sortedItineraries.length );
                setLoadingItinerary( false );
                setLoadingPricing( true );

                if ( isMyCarSearchEnabled ) {
                    const myCarSearchRequest: ItinerarySearchRequestDTO = {
                        "from": {
                            "lat":     fromLat,
                            "lon":     fromLon,
                            "country": fromCountry ?? "CH",
                        },
                        "to": {
                            "lat":     toLat,
                            "lon":     toLon,
                            "country": toCountry ?? "CH",
                        },
                        "time": {
                            "timestamp":            searchDate.timestamp,
                            "isDepartureTimestamp": searchDate.isDepartureTimestamp,
                        },
                        "modes":            [ TransportMode.MYCAR ],
                        "carItineraryType": "default",
                    };

                    const myCarPromise = itineraryService.searchItinerary( myCarSearchRequest, { "signal": abortController.signal } );

                    /*
                     * MYCAR does not depend on carpool or pricing; update the car row as soon as this response arrives
                     * instead of waiting for the other enrichments (previously felt like a delayed “car screen”).
                     */
                    void myCarPromise.
                        then( ( myCarResults ) => {
                            if ( !isCurrentRequest() ) {
                                return;
                            }

                            const myCarResultsArray = toSortedArray( myCarResults.itineraries );
                            const validMyCar = myCarResultsArray.find( isCompleteMyCarItinerary );
                            setITakeMyCarData( validMyCar ?? null );
                        } ).
                        catch( ( reason: unknown ) => {
                            if ( !isCurrentRequest() || isAbortError( reason ) ) {
                                return;
                            }

                            console.error( "[ItineraryList] MyCar search failed", {
                                ...getCorrelationFlowSnapshot(),
                                reason,
                            } );
                            setITakeMyCarData( null );
                        } );
                } else {
                    setITakeMyCar( false );
                    setITakeMyCarData( null );
                }
                const completionTask = searchResponse.needCompletion === true
                    ? ( () => {
                        const requestPricingPreferences = pricingPreferencesRef.current;

                        return itineraryService.completeItineraries(
                            {
                                "itineraries":        searchResponse.itineraries,
                                "pricingPreferences": requestPricingPreferences,
                                "search":             searchRequest,
                            },
                            abortController.signal,
                        ).then( ( completionResponse ) => {
                            if ( !isCurrentRequest() ) {
                                return;
                            }

                            setItineraries( ( previousItineraries ) => {
                                return mergeItineraryPatchById(
                                    previousItineraries,
                                    completionResponse.completedItineraries,
                                );
                            } );
                        } );
                    } )()
                    : Promise.resolve();

                const pricingTask = itineraryService.evaluateItinerariesPricing(
                    searchResponse.itineraries,
                    pricingPreferencesRef.current,
                    abortController.signal,
                ).then( ( pricingResult: PricingEvaluationResponseDTO ) => {
                    if ( !isCurrentRequest() ) {
                        return;
                    }

                    setItineraries( ( previousItineraries ) => {
                        return mergeItineraryPatchById(
                            previousItineraries,
                            pricingResult.itineraries,
                        );
                    } );
                } );

                const [ completionResult, pricingResult ] = await Promise.allSettled( [
                    completionTask,
                    pricingTask,
                ] );

                const completionReason: unknown = completionResult.status === "rejected"
                    ? completionResult.reason
                    : null;
                if ( completionResult.status === "rejected" && isAbortError( completionReason ) === false && isCurrentRequest() ) {
                    console.error( "[ItineraryList] Completion failed", {
                        ...getCorrelationFlowSnapshot(),
                        "reason": completionReason,
                    } );
                }

                const pricingReason: unknown = pricingResult.status === "rejected"
                    ? pricingResult.reason
                    : null;
                if ( pricingResult.status === "rejected" && isAbortError( pricingReason ) === false && isCurrentRequest() ) {
                    console.error( "[ItineraryList] Pricing evaluation failed", {
                        ...getCorrelationFlowSnapshot(),
                        "reason": pricingReason,
                    } );
                }

                if ( isCurrentRequest() ) {
                    setLoadingPricing( false );
                }
            } catch( err: unknown ) {
                if ( isAbortError( err ) ) {
                    if ( isCurrentRequest() ) {
                        completeItinerarySearchLoading( 0 );
                        setLoadingItinerary( false );
                        setLoadingPricing( false );
                    }

                    return;
                }
                if ( !isCurrentRequest() ) {
                    return;
                }
                console.error( "[ItineraryList] Itinerary flow failed", err );
                setLoadingItinerary( false );
                setLoadingPricing( false );
                completeItinerarySearchLoading( 0 );
                setVisible( false );
                void presentRef.current( {
                    "message":  "Erreur de serveur, réessayez plus tard !",
                    "duration": 500,
                    "position": "bottom",
                    "color":    "danger",
                } );
            }
        };

        // Validate coordinates before making API calls
        if (
            selectedInputs.departure.lat !== 0 &&
            selectedInputs.departure.lon !== 0 &&
            selectedInputs.arrival.lat !== 0 &&
            selectedInputs.arrival.lon !== 0
        ) {
            setITakeMyCar( false );
            void loadData();
        } else {
            if ( activeRequestAbortControllerRef.current !== null ) {
                activeRequestAbortControllerRef.current.abort();
                activeRequestAbortControllerRef.current = null;
            }
            if ( transitPageAbortControllerRef.current !== null ) {
                transitPageAbortControllerRef.current.abort();
                transitPageAbortControllerRef.current = null;
            }
            transitPageLoadingRef.current = null;
            lastSearchRequestRef.current = null;
            earlierArriveByBoundRef.current = null;
            setItineraries( [] ); // Clear itineraries if coordinates are invalid
            setLoadingItinerary( false );
            setLoadingPricing( false );

            // Deferred past the effect body: synchronous setState trips `react-hooks/set-state-in-effect`.
            queueMicrotask( () => {
                setTransitPageLoading( null );
                setTransitPagePricingPendingIds( new Set() );
                setTransitPageNewBatch( null );
                setTransitPageExhausted( {
                    "earlier": false,
                    "later":   false,
                } );
            } );
        }

        return () => {
            if ( effectAbortController !== null ) {
                effectAbortController.abort();
            }
            if ( activeRequestAbortControllerRef.current === effectAbortController ) {
                activeRequestAbortControllerRef.current = null;
            }
            if ( transitPageAbortControllerRef.current !== null ) {
                transitPageAbortControllerRef.current.abort();
            }
        };
    }, [ bikePrefs.bikeSpeedKmh, bikePrefs.elevationCosts, bikePrefs.isPersonalBike, bikePrefs.transportPersonalBike, bikePrefs.endItineraryByBike, bikeSpeedKmh, carPrefs, completeItinerarySearchLoading, date, departureSelectedAddress?.source, displayCurrency, enabledRentalVehicleTypes, hasPersonalBike, hasRentalTypes, isMyCarSearchEnabled, onResetPanelExpandedRef, persistenceReady, selectedAddress?.source, selectedInputs.arrival.lat, selectedInputs.arrival.lon, selectedInputs.arrivalAddress, selectedInputs.departure.lat, selectedInputs.departure.lon, selectedInputs.departureAddress, setDate, setIsEditedDate, setITakeMyCar, setITakeMyCarData, setItineraries, setLoadingItinerary, setLoadingPricing, setSelectedItinerary, setShowArrowTop, setItineraryListContentMode, setVisible, transitPrefs.enabledModes, transitPrefs.maxTransfers ] );

    useEffect( () => {
        swissReductionSnapshotBeforeGaRef.current = null;
    }, [ selectedItinerary?.id ] );

    useEffect( () => {
        const previousSwissReductionCard = previousSwissReductionCardRef.current;
        previousSwissReductionCardRef.current = selectedSwissReductionCard;

        if ( previousSwissReductionCard === selectedSwissReductionCard ) {
            return;
        }

        if ( selectedItinerary === null ) {
            return;
        }

        /*
         * GA: entering keeps the cached offer; store the non-GA reduction we came from. Leaving GA
         * back to that same reduction skips a refetch; any other non-GA target reloads the offer.
         */
        if ( selectedSwissReductionCard === "ga" && previousSwissReductionCard !== "ga" ) {
            swissReductionSnapshotBeforeGaRef.current = previousSwissReductionCard;

            return;
        }

        if ( selectedSwissReductionCard === "ga" ) {
            return;
        }

        if ( isSavedListMode ) {
            return;
        }

        /*
         * Prefs edits inside the pricing mini-modal are batched until dismiss (same as list
         * reevaluation). Refetching here would strip/reload the details offer immediately and
         * again on panel close.
         */
        if ( isPricingPrefsPanelOpen ) {
            return;
        }

        if ( previousSwissReductionCard === "ga" ) {
            const baseline = swissReductionSnapshotBeforeGaRef.current;
            if ( baseline !== null && selectedSwissReductionCard === baseline ) {
                return;
            }
        }

        const strippedSelected = stripItineraryPricingAndOfferData( selectedItinerary );
        setItineraries( ( previousItineraries ) => {
            return replaceItineraryInList( previousItineraries, strippedSelected );
        } );
        setSelectedItinerary( strippedSelected );
        selectedItineraryRef.current = strippedSelected;

        /*
         * Defer local offer-status reset: synchronous setState in this effect trips
         * `react-hooks/set-state-in-effect`. Microtask still clears before paint.
         */
        queueMicrotask( () => {
            setOfferStatusByItineraryId( ( previousStatusById ) => ( {
                ...previousStatusById,
                [strippedSelected.id]: "idle",
            } ) );
            requestOfferForItinerary( strippedSelected );
        } );
    }, [
        isPricingPrefsPanelOpen,
        isSavedListMode,
        requestOfferForItinerary,
        selectedSwissReductionCard,
        selectedItinerary,
        setItineraries,
        setSelectedItinerary,
    ] );

    const selectedItineraryOfferStatus: OfferFetchStatus = selectedItinerary !== null
        ? offerStatusByItineraryId[selectedItinerary.id] ?? ( selectedItinerary.offer !== undefined ? "success" : "idle" )
        : "idle";

    return {
        loading,
        loadingItinerary,
        loadingPricing,
        travelProgressRevealActive,
        skipListViewEnterAfterSearch,
        setSkipListViewEnterAfterSearch,
        offerStatusByItineraryId,
        setOfferStatusByItineraryId,
        selectedItineraryOfferStatus,
        itineraryPricingFromPrefs,
        wearCostPerKmChf,
        hasValidCoordinates,
        requestOfferForItinerary,
        itineraryDebugRelaunch,
        presentErrorToast,
        presentSuccessToast,
        loadTransitPage,
        transitPageLoading,
        transitPageNewBatch,
        transitPagePricingPendingIds,
        "transitPageExhaustedEarlier": transitPageExhausted.earlier,
        "transitPageExhaustedLater":   transitPageExhausted.later,
    };
};
